import React, {
  useEffect,
  useContext,
  useCallback,
  useState,
  useMemo,
  useRef,
} from 'react';
import { PropTypes } from 'prop-types';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import format from 'string-template';
import moment from 'moment';

import { ContractStatus, getContractObj } from '../../../../../CoachTools/utils/userContract';
import { generateTerms } from '../../../../../CoachTools/utils/userContractTerms';
import UserContractContext from '../../../../../context/UserContractContext';
import CoachAssignmentContext from '../../../../../context/CoachAssignmentContext';
import OnboardingFlowContext, { SignInLinkStatus } from '../../../../../context/OnboardingFlowContext';
import PlanContext from '../../../../../context/PlanContext';
import useSessionStore from '../../../../../hooks/useSessionStore';
import useAuthentication from '../../../../../hooks/useAuthentication';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useStorage from '../../../../../hooks/useStorage';
import { MONEY_DECIMALS, formatAmoutInCents, formatCurrencyCents } from '../../../../../utils/formatters';
import { DURATION_UNIT, formatPluralDuration } from '../../../../../utils/durationUnit';
import { getDeviceType } from '../../../../../CoachTools/components/ClientQuickInfoModal/utils';
import { useTwoPageCheckoutEnabled } from '../../../../../utils/postHog';
import subscriptionSubmissionTypes from '../../subscriptionSubmissionTypes';
import AppCustomization from '../../../../../CoachTools/Model/AppCustomization';
import LoadingPage from '../../../../../components/LoadingPage';
import Product from '../../../../../Model/Product';
import { ColorModes } from '../../../../../Model/Coach';

import ConfirmDialog from '../../../../../CoachTools/components/ConfirmDialog';
import colors from '../../../../../styles/colors';

import {
  SubscriptionCreationState,
} from '../../SubscriptionMetadata';

import FailureNotificationCard from '../FailureNotificationCard';
import PaymentForm from '../PaymentForm';
import TermsAgreement from '../TermsAgreement';
import UpsellAddedCard from '../UpsellAddedCard';
import PostPaymentUpsellOffer from '../PostPaymentUpsellOffer';

import ShareASale from '../ShareASale';
import CheckoutActions from '../CheckoutActions';
import AccountCreation from '../AccountCreation';

import SubmitButton from '../SubmitButton';

import { StyledLinkButton, ResetPasswordMessage } from '../AccountCreation/styles';
import {
  Container,
  StyledOnboardingCard,
  FreeTitle,
  InfoContainer,
  ClockIcon,
  WarningContainer,
  BellCrossedIcon,
  Step,
  StepContainer,
  AgreementContainer,
  StyledContractDetails,
  NoPaymentContainer,
  HeaderContainer,
  CoachHero,
  LeftSideWrapper,
  RightSideWrapper,
  StepTitle,
  StepSubTitle,
  StyledAppIcon,
  BillingDetails,
  MonthlyBillingContainer,
  RecurringPaymentContainer,
  MonthlyPriceContainer,
  MonthText,
  ProductName,
  PackageInfos,
  PackageInfo,
  ImportantNotes,
  ImportantNotesTitle,
  StyledListItem,
  UserDetailsSection,
  LogoutButton,
  UserName,
  StyledList,
  StyledWarningIcon,
  StyledDoneIcon,
  DesktopView,
  MobileView,
  StyledTabs,
  SignInLinkPanel,
  SignInLinkText,
  SignInLinkActions,
} from './styles';

import texts from './texts.json';

const RecurringOption = {
  YEARLY: 12,
  MONTHLY: 1,
};

const SubscriptionCard = ({ subscriptionFormData }) => {
  const {
    subscriptionControllerState,
    subscriptionState,
    applePayPaymentRequest,
    handleSubscriptionEndResult,
    stripeErrorState,
    handleSubscriptionSubmissionWithCreditCard,
    submitAuthenticationConfirmation,
    handleNoPaymentSubscriptionSubmission,
    onAccountFormSubmitted,
    accountCreationState,
    resetAccountCreationState,
    isOnboardingCompleted,
    automaticTaxEnabled,
    billingAddress,
    onBillingAddressChange,
    checkoutUpsell,
    userDetail: {
      firstName,
      lastName,
      email,
      phoneNumber,
    },
  } = subscriptionFormData;

  const [showPaymentConfirmationModal, setShowPaymentConfirmationModal] = useState(false);
  const [authConfirmationDetails, setAuthConfirmationDetails] = useState(null);
  const [subscriptionFlowError, setErrorState] = useState({});
  const [coachAppIconUrl, setCoachAppIconUrl] = useState('');
  const [coachAppCustomization, setCoachAppCustomization] = useState({});
  const [product, setProduct] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(subscriptionSubmissionTypes.CARD_PAYMENT);
  const isSignInMode = !!accountCreationState?.showForgotPassword;

  // Contract-related state
  const {
    contractDoc,
    newTermsAvailable,
    secondaryContractDoc,
    selectedContractDoc,
    selectedContractDocId,
    setSelectedContractDocId,
  } = useContext(UserContractContext);
  const [contract, setContract] = useState(null);

  const confirmPaymentForSubscription = useCallback((isConfirmed) => {
    submitAuthenticationConfirmation({ isConfirmed });
  }, [submitAuthenticationConfirmation]);

  useEffect(() => {
    if (subscriptionState === SubscriptionCreationState.WAITING_FOR_INITIAL_PAYMENT_AUTHORIZATION) {
      setShowPaymentConfirmationModal(true);
    }
  }, [subscriptionState]);

  useEffect(() => {
    let errorTmp = {
      isError: false,
      responseErrorMsg: null,
    };
    if (subscriptionState === SubscriptionCreationState.WAITING_FOR_VALID_PAYMENT_METHOD) {
      errorTmp = {
        isError: true,
        responseErrorMsg: 'Subscription payment validation failed. Please use a different card.',
      };
    }
    if (stripeErrorState && stripeErrorState.message) {
      errorTmp = {
        isError: true,
        responseErrorMsg: stripeErrorState.message,
      };
    }
    setErrorState(errorTmp);
  }, [subscriptionState, stripeErrorState]);

  useEffect(() => {
    if (authConfirmationDetails) {
      setShowPaymentConfirmationModal(false);
      confirmPaymentForSubscription(authConfirmationDetails.isConfirmed);
      setAuthConfirmationDetails(null);
    }
  }, [authConfirmationDetails, confirmPaymentForSubscription]);

  const onAuthConfirmationResponse = useCallback((isConfirmed) => {
    setShowPaymentConfirmationModal(false);
    setAuthConfirmationDetails({ isConfirmed });
  }, [setShowPaymentConfirmationModal, setAuthConfirmationDetails]);

  const { authUser } = useSessionStore();
  const { signOut } = useAuthentication();
  const isComponentMountedRef = useComponentMounted();
  const { getBlobUrl } = useStorage();

  const [isProceed, setIsProceed] = useState(!!authUser);

  const {
    currentCoach,
  } = useContext(CoachAssignmentContext);

  useEffect(() => {
    const fetchCoachConfig = async () => {
      const appCustomization = await AppCustomization.getAppCustomizationByCoach(currentCoach.id);
      const appIconUrl = await getBlobUrl(appCustomization.webAppIconRef);
      if (isComponentMountedRef.current) {
        setCoachAppCustomization(appCustomization);
        setCoachAppIconUrl(appIconUrl);
      }
    };
    fetchCoachConfig();
  }, [currentCoach, isComponentMountedRef, getBlobUrl]);

  const isDarkTheme = currentCoach.checkoutPageColorMode === ColorModes.DARK;
  const isDesktopView = getDeviceType() === 'DESKTOP';

  const [isAcceptedTerms, setIsAcceptedTerms] = useState(false);

  const dialogStyles = {
    '& .MuiPaper-root': {
      background: isDarkTheme ? colors.base.beta : colors.base.alpha,
    },
    '& .MuiDialogTitle-root': {
      color: isDarkTheme ? colors.base.alpha : colors.base.beta,
    },
    '& .MuiDialogContentText-root': {
      color: colors.shades.gamma10,
    },
  };

  const {
    startAtDate,
    signInLinkState,
    resendSignInLink,
    resetSignInLinkState,
  } = useContext(OnboardingFlowContext);

  const {
    plan: {
      isFree: isFreePlan,
      totalPriceInCents,
      initialPaymentInCents,
      recurringBillingMonths,
      cancelAt,
      currency,
      cancelAtPeriodEnd,
      id: priceId,
      minSubscriptionMonths,
      initialTerm = recurringBillingMonths,
    },
  } = useContext(PlanContext);

  // Create the contract object so that we can display the whole list of terms for every subscription.
  useEffect(() => {
    let contractObj;
    if (selectedContractDoc) {
      contractObj = getContractObj(selectedContractDoc);
    } else {
      // TODO: Remove this once we remove support for lagacy subscriptions (PIF and CHALLENGES using onboarding links).
      contractObj = {
        startDate: startAtDate || moment(),
        initialPayment: formatAmoutInCents(totalPriceInCents, MONEY_DECIMALS),
        monthlyPayment: formatAmoutInCents(totalPriceInCents, MONEY_DECIMALS),
        initialTerm: recurringBillingMonths || initialTerm,
        minSubscriptionMonths: minSubscriptionMonths || 3,
        currency,
        priceId,
        cancelAtPeriodEnd,
        coachConditions: currentCoach.termsAndConditionsText,
        /*
          If there is no contract, then it is a legacy plan, so we force the version to 1. Version 1 contracts allow
          for PIF contracts, for example, and challenges. In those cases, we create a contract in the server once the
          subscription is created.
        */
        version: 1,
      };
    }

    // If the start date is in the past, we set it to today in the checkout page, and we remove the upfront payment.
    if (contractObj.startDate.isBefore(moment())) {
      contractObj.startDate = moment();
      contractObj.upfrontPayment = 0;
      /*
        Flag rather than re-deriving the condition at render: the stored terms
        were written against the original date and can no longer describe this
        contract, so they have to be regenerated. Recomputing the same
        comparison in a second place is how the two drift apart.
      */
      contractObj.wasReDatedByCheckout = true;
    }

    setContract(contractObj);
  }, [
    selectedContractDoc,
    currency,
    priceId,
    startAtDate,
    totalPriceInCents,
    cancelAtPeriodEnd,
    minSubscriptionMonths,
    currentCoach.termsAndConditionsText,
    initialTerm,
    recurringBillingMonths,
  ]);

  /*
    Show the terms stored on the contract when there is one. Those are the
    exact strings the server records as accepted, and the app that created
    the contract may not be this one — a launchpad-created challenge stores
    its own wording, which regenerating here would silently paper over.

    Except when the checkout has re-dated the contract: a start date that
    has already passed is moved to today and the upfront payment dropped
    (see the effect above), and the stored strings were written against the
    original date. Showing them then would name an advance payment on a day
    that has gone and a start date that never happens, while the form
    charges the full initial payment today. Regenerating keeps the displayed
    terms matching what is actually billed, which matters more here than
    preserving the authoring app's wording.
  */
  const contractTerms = useMemo(() => {
    if (!contract) {
      return null;
    }
    return !contract.wasReDatedByCheckout && selectedContractDoc?.terms?.length
      ? selectedContractDoc.terms
      : generateTerms(contract);
  }, [contract, selectedContractDoc]);

  useEffect(() => {
    const fetchProduct = async () => {
      const productDoc = new Product(contractDoc.productId);
      await productDoc.init();
      setProduct(productDoc);
    };
    if (contractDoc) {
      fetchProduct();
    }
  }, [contractDoc]);

  const isChallenge = useMemo(() => !!contract?.challengeId, [contract]);

  /*
    Challenges can be sold by the week, in which case initialTerm /
    minSubscriptionMonths are counted in weeks. Read the unit off the contract
    document rather than the derived contract object so it survives the legacy
    branch above, which has no unit and means months.
  */
  const durationUnit = selectedContractDoc?.durationUnit || DURATION_UNIT.MONTH;

  /*
    Whether the price is followed by a duration ("for 6 weeks") rather than a
    bare recurring period. Derived once because it drives both the copy and the
    gap between the two — a challenge with a single-unit term reads
    "$99for 1 week" if only one of them knows about it.
  */
  const showDuration = isChallenge || initialTerm > 1;

  const chatCoach = product?.isAiFriendEnabled ? 'AI coach' : 'coach';

  // Prepare form components based on plan pricing (free or not)
  const onSubmit = isFreePlan ? handleNoPaymentSubscriptionSubmission : handleSubscriptionSubmissionWithCreditCard;

  /*
    Guarded on isFreePlan and not just the contract flag: skipping terms means
    the user never confirms anything past account creation, which is only safe
    when there is nothing to charge — a paid contract carrying the flag would
    silently bypass its own terms.
  */
  const skipTerms = !!selectedContractDoc?.skipTerms && !!isFreePlan;

  /*
    The two-page checkout takes the terms on its first page — a tick box with the terms in a modal, on the account
    form or, for a client already signed in, on its own — and goes straight on to payment. A skip-terms link has no
    terms to take and keeps its own one-step flow.

    The agreement is held as the id of the contract agreed to, so switching a lead's contract option after agreeing
    asks again: agreeing to one contract's terms is not agreeing to the other's.
  */
  const isTwoPageCheckout = useTwoPageCheckoutEnabled(currentCoach.id) && !skipTerms;
  const termsKey = selectedContractDoc?.id || 'plan';
  const [agreedTermsKey, setAgreedTermsKey] = useState(null);
  const hasAgreedToTerms = agreedTermsKey === termsKey;
  // Ticked on the signed-in first page, before the client continues; a tick given to other terms is not kept.
  const [isTermsBoxChecked, setIsTermsBoxChecked] = useState(false);
  useEffect(() => {
    setIsTermsBoxChecked(false);
  }, [termsKey]);
  const isPaymentStep = isTwoPageCheckout ? hasAgreedToTerms : isAcceptedTerms;

  /*
    Steps: create account, accept terms, pay — free plans skip payment, and skip-terms links keep only the first.
    The two-page checkout folds the terms into the first.
  */
  let totalSteps;
  if (isTwoPageCheckout) {
    totalSteps = isFreePlan ? 1 : 2;
  } else if (isFreePlan) {
    totalSteps = skipTerms ? 1 : 2;
  } else {
    totalSteps = 3;
  }
  const paymentStepNumber = isTwoPageCheckout ? 2 : 3;

  // Two-page free plans sign up straight from the first page, as skip-terms links do from the account form.
  const isSubmittedWithoutPayment = skipTerms || (isTwoPageCheckout && isFreePlan && hasAgreedToTerms);

  /*
    The auto-submit below must only follow an explicit action taken on this
    visit. A restored session makes authUser truthy on plain page load, so
    keying off authentication alone would subscribe an already-signed-in
    visitor (and reassign their coach) just for opening the link; they get a
    Join button instead.
  */
  const [hasSubmittedAccountForm, setHasSubmittedAccountForm] = useState(false);

  /*
    Signing in can land before the account step has decided: an existing account is signed in first and only then
    checked against the returning-client rules, which can sign it straight back out. Until the submission settles the
    client is still on the account step, so nothing past it shows for a moment and disappears.
  */
  const [isAccountSubmissionPending, setIsAccountSubmissionPending] = useState(false);
  const isSignedInForCheckout = !!authUser && !isAccountSubmissionPending;

  const onAccountFormSubmittedForContract = useCallback(async (formData) => {
    setHasSubmittedAccountForm(true);
    if (isTwoPageCheckout && formData.hasAgreedToTerms) {
      setAgreedTermsKey(termsKey);
    }
    setIsAccountSubmissionPending(true);
    try {
      return await onAccountFormSubmitted(formData);
    } finally {
      if (isComponentMountedRef.current) {
        setIsAccountSubmissionPending(false);
      }
    }
  }, [onAccountFormSubmitted, isTwoPageCheckout, termsKey, isComponentMountedRef]);

  /*
    With no terms step — or with the terms already agreed on the account form
    of a free two-page checkout — there is nothing left for the user to do
    after signing up, so the subscription is submitted for them. Once per mount: a failure
    must land on the failure card, not re-trigger a loop.
  */
  const hasAutoSubmittedRef = useRef(false);
  useEffect(() => {
    if (isSubmittedWithoutPayment
      && isSignedInForCheckout
      && hasSubmittedAccountForm
      && subscriptionState === SubscriptionCreationState.INITIAL
      && !subscriptionControllerState.isBusy
      && !hasAutoSubmittedRef.current) {
      hasAutoSubmittedRef.current = true;
      handleNoPaymentSubscriptionSubmission();
    }
  }, [
    isSubmittedWithoutPayment,
    isSignedInForCheckout,
    hasSubmittedAccountForm,
    subscriptionState,
    subscriptionControllerState,
    handleNoPaymentSubscriptionSubmission,
  ]);

  /*
    Subscription form is loading when subscription creation is not complete and the controller is busy, or when the
    subscription creation is complete but the user onboarding is not complete yet
  */
  const subscriptionIsLoading = useMemo(() => (
    (subscriptionState !== SubscriptionCreationState.SUCCESSFUL_COMPLETION && subscriptionControllerState.isBusy)
    || (subscriptionState === SubscriptionCreationState.SUCCESSFUL_COMPLETION && !isOnboardingCompleted)
  ), [
    subscriptionState,
    subscriptionControllerState,
    isOnboardingCompleted,
  ]);

  const hasAddedCoaching = !!checkoutUpsell?.hasAddedCoaching;
  // What Home gets, whichever way the client leaves checkout.
  const subscriptionEndResult = useMemo(() => ({
    appIconUrl: coachAppIconUrl,
    color: coachAppCustomization?.primaryColor,
    isDarkTheme,
    /*
      Home offers a challenge's commitment sticker before the app download. Whether the coach turned sharing on
      is read there, off the challenge's BASE contract, never off this document: a one-off or copied contract
      carries its own stale copy of the flag. Read `challengeId` off the contract document, not `contract`: that
      is only filled in after the first render, and a client whose checkout is already done moves on in that
      render. `addedCoaching`: whether the client took the coach's 1:1 at this checkout, for the sticker's events.
    */
    ...(selectedContractDoc?.challengeId && {
      storyShare: { challengeId: selectedContractDoc.challengeId, addedCoaching: hasAddedCoaching },
    }),
  }), [
    coachAppIconUrl,
    coachAppCustomization?.primaryColor,
    isDarkTheme,
    selectedContractDoc,
    hasAddedCoaching,
  ]);

  const handleSubscriptionEnd = useCallback(() => {
    handleSubscriptionEndResult(subscriptionEndResult);
  }, [handleSubscriptionEndResult, subscriptionEndResult]);

  /*
    Once onboarding completes there is nothing left to confirm, after a purchase or for a client already subscribed:
    the client goes straight on to download the app. The control arm's 1:1 offer comes first when there is one (it
    moves on itself), so a purchase waits until it is known whether there is. The Download the App page reports a
    new purchase to ShareASale.
  */
  const isNewPurchase = subscriptionState === SubscriptionCreationState.SUCCESSFUL_COMPLETION;
  const isOfferedAfterPayment = isNewPurchase && !!checkoutUpsell?.postPayment.isAvailable;
  const isCheckoutDone = isOnboardingCompleted && (
    (isNewPurchase && !isOfferedAfterPayment && (checkoutUpsell?.postPayment.isSettled ?? true))
    || subscriptionState === SubscriptionCreationState.ACTIVE_SUBSCRIPTION_EXISTS
  );
  const hasMovedOnRef = useRef(false);
  useEffect(() => {
    if (isCheckoutDone && !hasMovedOnRef.current) {
      hasMovedOnRef.current = true;
      handleSubscriptionEndResult({ ...subscriptionEndResult, isNewPurchase });
    }
  }, [
    isCheckoutDone,
    isNewPurchase,
    handleSubscriptionEndResult,
    subscriptionEndResult,
  ]);

  // The 1:1 the client accepted on the challenge's landing page, on the payment step with the coach's offer.
  const renderUpsellAddedCard = useCallback(() => (
    checkoutUpsell?.landing.isShown ? (
      <UpsellAddedCard
        offer={checkoutUpsell.offer}
        challengeEndDate={checkoutUpsell.challengeEndDate}
        isAdded={checkoutUpsell.landing.isAdded}
        onRemove={checkoutUpsell.landing.remove}
        onAddBack={checkoutUpsell.landing.addBack}
        onShown={checkoutUpsell.landing.logShown}
        isDarkTheme={isDarkTheme}
      />
    ) : null
  ), [checkoutUpsell, isDarkTheme]);

  const renderAccountCreationStep = useCallback(() => {
    if (isSignedInForCheckout) {
      return null;
    }

    const { status: signInLinkStatus, email: signInLinkEmail } = signInLinkState;
    // Someone arriving on a link is already past this step; showing it would flash the empty form.
    if (signInLinkStatus === SignInLinkStatus.SIGNING_IN) {
      return null;
    }

    const isLinkSent = signInLinkStatus === SignInLinkStatus.SENT;
    const isLinkExpired = signInLinkStatus === SignInLinkStatus.EXPIRED;
    const isLinkRefused = signInLinkStatus === SignInLinkStatus.REFUSED;
    const isLinkFailed = signInLinkStatus === SignInLinkStatus.FAILED;
    const isLinkPanel = isLinkSent || isLinkExpired || isLinkRefused || isLinkFailed;

    return (
      <StepContainer>
        <Step
          $background={coachAppCustomization?.primaryColor}
          $isDarkTheme={isDarkTheme}
        >
          {format(texts.step, { number: '1', total: totalSteps })}
        </Step>
        <StepTitle $isDarkTheme={isDarkTheme}>
          {isLinkRefused && texts.signInLinkRefusedTitle}
          {isLinkFailed && texts.signInLinkFailedTitle}
          {(isLinkSent || isLinkExpired) && texts.signInLinkTitle}
          {!isLinkPanel && (isSignInMode ? texts.signIn : texts.createAccount)}
        </StepTitle>
        {!isLinkPanel && (
          <StepSubTitle>{isSignInMode ? texts.signInContent : texts.signUpContent}</StepSubTitle>
        )}
        {isLinkPanel ? (
          <SignInLinkPanel>
            <SignInLinkText $isDarkTheme={isDarkTheme}>
              {(isLinkRefused || isLinkFailed) && signInLinkState.message}
              {isLinkExpired && texts.signInLinkExpiredBody}
              {isLinkSent && format(texts.signInLinkSent, { email: signInLinkEmail })}
            </SignInLinkText>
            {!!signInLinkState.resendResult && (
              <ResetPasswordMessage $isError={signInLinkState.resendResult === 'failed'}>
                {signInLinkState.resendResult === 'failed'
                  ? texts.signInLinkFailed
                  : format(texts.signInLinkResent, { email: signInLinkEmail })}
              </ResetPasswordMessage>
            )}
            <SignInLinkActions>
              {!isLinkRefused && (
                <StyledLinkButton
                  type="button"
                  disabled={signInLinkState.isSending}
                  onClick={resendSignInLink}
                  $isDarkTheme={isDarkTheme}
                >
                  {texts.resendSignInLink}
                </StyledLinkButton>
              )}
              <StyledLinkButton
                type="button"
                onClick={resetSignInLinkState}
                $isDarkTheme={isDarkTheme}
              >
                {texts.changeEmail}
              </StyledLinkButton>
            </SignInLinkActions>
          </SignInLinkPanel>
        ) : (
          <>
            <AccountCreation
              email={email}
              firstName={firstName}
              lastName={lastName}
              phoneNumber={phoneNumber}
              onSubmit={onAccountFormSubmittedForContract}
              state={accountCreationState}
              color={coachAppCustomization?.primaryColor}
              isDarkTheme={isDarkTheme}
              isSignInMode={isSignInMode}
              onChangeEmail={resetAccountCreationState}
              termsAgreement={isTwoPageCheckout && contractTerms
                ? { contractTerms, coachConditions: contract.coachConditions, termsKey }
                : null}
            />
          </>
        )}
      </StepContainer>
    );
  }, [
    isSignedInForCheckout,
    email,
    firstName,
    lastName,
    phoneNumber,
    onAccountFormSubmittedForContract,
    accountCreationState,
    coachAppCustomization,
    isDarkTheme,
    totalSteps,
    isSignInMode,
    signInLinkState,
    resendSignInLink,
    resetSignInLinkState,
    resetAccountCreationState,
    isTwoPageCheckout,
    contractTerms,
    contract,
    termsKey,
  ]);

  const renderCheckoutStep = useCallback(() => {
    if (isSignedInForCheckout) {
      /*
        No terms or payment step to render. After an in-session signup the
        auto-submit effect is already creating the subscription, so only
        progress shows; a visitor who arrived already signed in confirms with
        one explicit tap instead.
      */
      if (isSubmittedWithoutPayment) {
        const isSubmitting = hasSubmittedAccountForm || subscriptionIsLoading;
        return (
          <StepContainer>
            <StepTitle $isDarkTheme={isDarkTheme}>
              {isSubmitting ? texts.settingUpSubscription : texts.joinTitle}
            </StepTitle>
            <NoPaymentContainer>
              <SubmitButton
                isLoading={isSubmitting}
                onClick={onSubmit}
                $background={coachAppCustomization?.primaryColor}
                $isDarkTheme={isDarkTheme}
              >
                {isSubmitting ? texts.settingUpSubscription : texts.joinCta}
              </SubmitButton>
            </NoPaymentContainer>
          </StepContainer>
        );
      }

      if (isPaymentStep) {
        return (
          <StepContainer>
            <Step
              $background={coachAppCustomization?.primaryColor}
              $isDarkTheme={isDarkTheme}
            >
              {format(texts.step, { number: String(paymentStepNumber), total: totalSteps })}
            </Step>
            <StepTitle $isDarkTheme={isDarkTheme}>
              {texts.checkout}
            </StepTitle>
            <StepSubTitle />
            {renderUpsellAddedCard()}
            {!!applePayPaymentRequest && (
              <CheckoutActions
                selectedPaymentType={selectedPaymentMethod}
                setSelectedPaymentType={setSelectedPaymentMethod}
                isDarkTheme={isDarkTheme}
                applePayPaymentRequest={applePayPaymentRequest}
              />
            )}
            <PaymentForm
              isLoading={subscriptionIsLoading}
              errorMessage={subscriptionFlowError?.responseErrorMsg}
              onSubmit={onSubmit}
              isDarkTheme={isDarkTheme}
              color={coachAppCustomization?.primaryColor}
              contract={contract}
              isApplePayView={selectedPaymentMethod === subscriptionSubmissionTypes.APPLE_PAY}
              automaticTaxEnabled={automaticTaxEnabled}
              billingAddress={billingAddress}
              onBillingAddressChange={onBillingAddressChange}
            />
          </StepContainer>
        );
      }

      /*
        `contract` is built by an effect, so it is still null on the first render
        pass — and this component mounts with an already-restored auth session
        whenever the lead has been here before, which lands that first pass right
        here. Everything below describes the contract being accepted, so there is
        nothing to show until it exists; reading through it instead throws during
        render and unmounts the whole onboarding page to a blank screen.
      */
      if (!contract) {
        return null;
      }

      /*
        A client already signed in skips the account form, so on the two-page
        checkout their first page is the terms on their own: agreeing goes on
        to payment, or signs a free plan up.
      */
      if (isTwoPageCheckout) {
        const agreeToTerms = () => {
          setAgreedTermsKey(termsKey);
          if (isFreePlan) {
            onSubmit();
          }
        };
        return (
          <StepContainer>
            <Step
              $background={coachAppCustomization?.primaryColor}
              $isDarkTheme={isDarkTheme}
            >
              {format(texts.step, { number: '1', total: totalSteps })}
            </Step>
            <StepTitle $isDarkTheme={isDarkTheme}>
              {texts.termsOfService}
            </StepTitle>
            <StepSubTitle>{texts.termsAgreementContent}</StepSubTitle>
            <TermsAgreement
              isChecked={isTermsBoxChecked}
              onChange={setIsTermsBoxChecked}
              contractTerms={contractTerms}
              coachConditions={contract.coachConditions}
              color={coachAppCustomization?.primaryColor}
              isDarkTheme={isDarkTheme}
            />
            <NoPaymentContainer>
              <SubmitButton
                onClick={agreeToTerms}
                disabled={!isTermsBoxChecked}
                isLoading={subscriptionIsLoading}
                $background={coachAppCustomization?.primaryColor}
                $isDarkTheme={isDarkTheme}
              >
                {isFreePlan ? texts.joinCta : texts.continueToPayment}
              </SubmitButton>
            </NoPaymentContainer>
          </StepContainer>
        );
      }

      return (
        <StepContainer>
          <Step
            $background={coachAppCustomization?.primaryColor}
            $isDarkTheme={isDarkTheme}
          >
            {format(texts.step, { number: '2', total: totalSteps })}
          </Step>
          <StepTitle $isDarkTheme={isDarkTheme}>
            {texts.termsOfService}
          </StepTitle>
          <StepSubTitle>{texts.termsContent}</StepSubTitle>
          <AgreementContainer>
            <StyledContractDetails
              contractTerms={contractTerms}
              coachConditions={contract.coachConditions}
              isDarkTheme={isDarkTheme}
              isCheckoutPageview
            />
          </AgreementContainer>
          {
            isFreePlan
              ? (
                <NoPaymentContainer>
                  <SubmitButton
                    onClick={onSubmit}
                    isLoading={subscriptionIsLoading}
                    $background={coachAppCustomization?.primaryColor}
                    $isDarkTheme={isDarkTheme}
                  >
                    {texts.acceptAndContinue}
                  </SubmitButton>
                </NoPaymentContainer>
              ) : (
                <SubmitButton
                  $background={coachAppCustomization?.primaryColor}
                  $isDarkTheme={isDarkTheme}
                  onClick={() => setIsAcceptedTerms(true)}
                >
                  {texts.acceptAndContinue}
                </SubmitButton>
              )
          }
        </StepContainer>
      );
    }
    return null;
  }, [
    isSignedInForCheckout,
    subscriptionFlowError,
    onSubmit,
    subscriptionIsLoading,
    isFreePlan,
    hasSubmittedAccountForm,
    totalSteps,
    contract,
    coachAppCustomization,
    isDarkTheme,
    selectedPaymentMethod,
    applePayPaymentRequest,
    automaticTaxEnabled,
    billingAddress,
    onBillingAddressChange,
    isSubmittedWithoutPayment,
    isPaymentStep,
    paymentStepNumber,
    isTwoPageCheckout,
    termsKey,
    isTermsBoxChecked,
    contractTerms,
    renderUpsellAddedCard,
  ]);

  const renderContractTabs = useCallback(() => {
    /*
      skipTerms flows never reach the payment step, so gate on the flag too:
      switching contracts while the auto-submit is in flight would race the
      subscription being created.
    */
    if (!contractDoc || !secondaryContractDoc || isPaymentStep || skipTerms) {
      return null;
    }
    /*
      The choice sits with the terms it decides between: on the three-step
      checkout that is the terms step, after sign-in; on the two-page checkout
      it is the first page, before it.
    */
    if (!isTwoPageCheckout && !authUser) {
      return null;
    }

    return (
      <StyledTabs
        tabsConfig={{
          [contractDoc.id]: { label: texts.monthlyPlan },
          [secondaryContractDoc.id]: {
            label: format(texts.upfrontPlan, {
              discount: secondaryContractDoc.price.initialDiscountPercentage,
            }),
          },
        }}
        selectedTab={selectedContractDocId || contractDoc.id}
        onSelectTab={setSelectedContractDocId}
        $isDarkTheme={isDarkTheme}
        $background={coachAppCustomization?.primaryColor}
        variant="standard"
        centered
      />
    );
  }, [
    contractDoc,
    secondaryContractDoc,
    selectedContractDocId,
    setSelectedContractDocId,
    isPaymentStep,
    isTwoPageCheckout,
    skipTerms,
    authUser,
    isDarkTheme,
    coachAppCustomization,
  ]);

  const renderPackageDetails = useCallback(() => (
    <>
      <CoachHero src={currentCoach.avatar} />
      <BillingDetails>
        {(!!authUser || isChallenge) && (
        <MonthlyBillingContainer $isDarkTheme={isDarkTheme}>
          <ProductName>{product?.name || texts.oneOnOneCoaching}</ProductName>
          <MonthlyPriceContainer $addGap={showDuration}>
            <RecurringPaymentContainer $isDarkTheme={isDarkTheme}>
              {formatCurrencyCents(initialPaymentInCents || totalPriceInCents, currency)}
            </RecurringPaymentContainer>
            {showDuration ? (
              <MonthText>
                {/*
                  A challenge is prepaid access for a fixed term, so it always
                  reads "for N weeks/months" — including a single-unit one,
                  which would otherwise fall through to the recurring copy and
                  claim a monthly subscription.
                */}
                {format(texts.forDuration, { duration: formatPluralDuration(initialTerm, durationUnit) })}
              </MonthText>
            ) : (
              <MonthText>
                {recurringBillingMonths === RecurringOption.MONTHLY ? texts.month : texts.year}
              </MonthText>
            )}
          </MonthlyPriceContainer>
        </MonthlyBillingContainer>
        )}
        <PackageInfos $isDarkTheme={isDarkTheme}>
          {!isChallenge && (product?.isCheckInFeedBackEnabled || product?.isChatEnabled) && (
            <PackageInfo>
              <StyledDoneIcon />
              {
                product?.isAiFriendEnabled ? format(texts.aiFriendCoaching, { coachName: currentCoach.name })
                  : format(texts.oneToOneCoaching, { coachName: currentCoach.name })
              }
            </PackageInfo>
          )}
          {/* WARNING: This is a temporary solution to handle checkout page customization for a specific coach. */}
          {/* TODO: Refactor this when we add checkout page customization */}
          {coachAppCustomization?.isWorkoutEnabled && (
            <PackageInfo>
              <StyledDoneIcon />
              {isChallenge ? texts.challengeWorkout : texts.workout}
            </PackageInfo>
          )}
          {product?.isMealPlanEnabled && (
            <PackageInfo>
              <StyledDoneIcon />
              {/* WARNING: This is a temporary solution to handle the British spelling for a specific coach. */}
              {/* TODO: Refactor this when we add checkout page customization */}
              {(() => {
                if (isChallenge) {
                  return texts.challengeMealPlan;
                }
                return coachAppCustomization?.useBritishSpelling ? texts.britishMealPlan : texts.mealPlan;
              })()}
            </PackageInfo>
          )}
          {!isChallenge && product?.isCheckInFeedBackEnabled && (
            <PackageInfo>
              <StyledDoneIcon />
              {texts.checkInFeedback}
            </PackageInfo>
          )}
          <PackageInfo>
            <StyledDoneIcon />
            {isChallenge ? texts.challengeAccountabilityAndHabits : texts.accountabilityAndHabits}
          </PackageInfo>
          {!isChallenge && product?.isVideoFeedBackEnabled && (
            <PackageInfo>
              <StyledDoneIcon />
              {texts.videoFeedback}
            </PackageInfo>
          )}
          {product?.isChatEnabled && (
            <PackageInfo>
              <StyledDoneIcon />
              {isChallenge ? texts.challengeChat : format(texts.chat, { chatCoach })}
            </PackageInfo>
          )}
        </PackageInfos>
        <ImportantNotes>
          <ImportantNotesTitle>
            <StyledWarningIcon />
            {texts.importantInformation}
          </ImportantNotesTitle>
          <StyledList>
            {/*
              A challenge is a single up-front charge for a fixed access window,
              not a subscription with a commitment. Saying "N month(s) minimum
              commitment" misdescribes both the billing and the term, so
              challenges get their own two lines — matching the mobile app's
              ContractSummarySection.
            */}
            {isChallenge ? (
              <>
                <StyledListItem>{texts.oneTimePayment}</StyledListItem>
                <StyledListItem>
                  {format(texts.challengeAccess, {
                    duration: formatPluralDuration(minSubscriptionMonths, durationUnit),
                  })}
                </StyledListItem>
              </>
            ) : (
              <>
                {initialTerm === 1 && (
                  <StyledListItem>
                    {recurringBillingMonths === RecurringOption.MONTHLY ? texts.monthlyBilling : texts.annualBilling}
                  </StyledListItem>
                )}
                <StyledListItem>
                  {format(texts.minimumCommitment, {
                    months: minSubscriptionMonths,
                  })}
                </StyledListItem>
                <StyledListItem>{texts.autoRenewal}</StyledListItem>
              </>
            )}
          </StyledList>
        </ImportantNotes>
      </BillingDetails>
      {isFreePlan && (
        <FreeTitle $isDarkTheme={isDarkTheme}>{texts.freePlanDescription}</FreeTitle>
      )}
      {
        !!startAtDate && (
          <InfoContainer $isDarkTheme={isDarkTheme}>
            <ClockIcon />
            <span>{format(texts.billingCycleStart, startAtDate.format('LL'))}</span>
          </InfoContainer>
        )
      }
      {
        !!cancelAt && (
          <WarningContainer $isDarkTheme={isDarkTheme}>
            <BellCrossedIcon />
            <span>{format(texts.subscriptionEnds, cancelAt.format('LL'))}</span>
          </WarningContainer>
        )
      }
    </>
  ), [
    currentCoach,
    product,
    initialPaymentInCents,
    recurringBillingMonths,
    minSubscriptionMonths,
    isFreePlan,
    startAtDate,
    cancelAt,
    isDarkTheme,
    currency,
    initialTerm,
    totalPriceInCents,
    authUser,
    coachAppCustomization,
    isChallenge,
    chatCoach,
    durationUnit,
    showDuration,
  ]);

  if (subscriptionState === SubscriptionCreationState.FAILURE_COMPLETION
    || subscriptionState === SubscriptionCreationState.FAILURE_NETWORK) {
    return (
      <Container $isDarkTheme={isDarkTheme}>
        <FailureNotificationCard
          message={texts.notification.defaultFailure}
          onConfirmingFinalResult={handleSubscriptionEnd}
          isDarkTheme={isDarkTheme}
          color={coachAppCustomization?.primaryColor}
        />
      </Container>
    );
  }

  // Moving on to the Download the App page once onboarding completes.
  if (subscriptionState === SubscriptionCreationState.ACTIVE_SUBSCRIPTION_EXISTS) {
    return <LoadingPage />;
  }

  if ((contractDoc && contractDoc.exists)
    && (contractDoc.status === ContractStatus.FAILED)) {
    return (
      <Container $isDarkTheme={isDarkTheme}>
        <FailureNotificationCard
          message={texts.notification.defaultFailure}
          onConfirmingFinalResult={handleSubscriptionEnd}
          isDarkTheme={isDarkTheme}
          color={coachAppCustomization?.primaryColor}
        />
      </Container>
    );
  }

  if (isOnboardingCompleted) {
    /*
      The upsell test's control arm offers the coach's 1:1 before the client moves on, only after a payment just
      went through: there is no subscription to sign it up from otherwise. It reports the purchase to ShareASale
      itself, so it moves on without reporting it again.
    */
    if (isOfferedAfterPayment) {
      return (
        <Container $isDarkTheme={isDarkTheme}>
          <ShareASale
            userId={authUser.uid}
          />
          <PostPaymentUpsellOffer
            offer={checkoutUpsell.offer}
            paidAmount={formatCurrencyCents(
              checkoutUpsell.paidAmountInCents || initialPaymentInCents || totalPriceInCents,
              currency,
              MONEY_DECIMALS,
            )}
            challengeName={product?.name || texts.yourChallenge}
            challengeEndDate={checkoutUpsell.challengeEndDate}
            coachName={currentCoach.name}
            coachImageUrl={currentCoach.avatar}
            isDesktop={isDesktopView}
            isDarkTheme={isDarkTheme}
            color={coachAppCustomization?.primaryColor}
            onAccept={checkoutUpsell.acceptPostPaymentOffer}
            onDecline={checkoutUpsell.postPayment.decline}
            onShown={checkoutUpsell.postPayment.logShown}
            onDone={handleSubscriptionEnd}
          />
        </Container>
      );
    }
    // Moving on to the Download the App page.
    return <LoadingPage />;
  }

  return (
    <Container $isDarkTheme={isDarkTheme}>
      <HeaderContainer>
        {!!coachAppIconUrl && (
          <StyledAppIcon
            src={coachAppIconUrl}
            $isDisplay={!isProceed}
          />
        )}
        {!!authUser && !!coachAppIconUrl && (
          <UserDetailsSection>
            <UserName>
              {`${firstName} ${lastName}`}
            </UserName>
            <LogoutButton
              onClick={signOut}
              $isDarkTheme={isDarkTheme}
            >
              {texts.logOut}
            </LogoutButton>
          </UserDetailsSection>
        )}
      </HeaderContainer>
      <StyledOnboardingCard $isDarkTheme={isDarkTheme}>
        {renderContractTabs()}
        {isDesktopView ? (
          <DesktopView>
            <LeftSideWrapper $isDarkTheme={isDarkTheme}>
              {renderPackageDetails()}
            </LeftSideWrapper>
            <RightSideWrapper $isDarkTheme={isDarkTheme}>
              {renderAccountCreationStep()}
              {renderCheckoutStep()}
            </RightSideWrapper>
          </DesktopView>
        ) : (
          <MobileView>
            {(!isProceed && !authUser && !signInLinkState.status) ? (
              <LeftSideWrapper $isDarkTheme={isDarkTheme}>
                {renderPackageDetails()}
                <SubmitButton
                  $background={coachAppCustomization?.primaryColor}
                  $isDarkTheme={isDarkTheme}
                  onClick={() => setIsProceed(true)}
                >
                  {texts.proceed}
                </SubmitButton>
              </LeftSideWrapper>
            ) : (
              <RightSideWrapper $isDarkTheme={isDarkTheme}>
                {renderAccountCreationStep()}
                {renderCheckoutStep()}
              </RightSideWrapper>
            )}
          </MobileView>
        )}
      </StyledOnboardingCard>
      <ConfirmDialog
        isOpen={showPaymentConfirmationModal}
        onConfirm={() => onAuthConfirmationResponse(true)}
        onCancel={() => onAuthConfirmationResponse(false)}
        dialogTexts={{
          title: texts.confirmDialog.title,
          content: texts.confirmDialog.content,
        }}
        sx={dialogStyles}
      />
      <ConfirmDialog
        isOpen={newTermsAvailable}
        onConfirm={() => window.location.reload()}
        dialogTexts={{
          title: texts.refreshDialog.title,
          content: texts.refreshDialog.content,
          confirm: texts.refreshDialog.confirm,
        }}
        sx={dialogStyles}
      />
    </Container>
  );
};

SubscriptionCard.propTypes = {
  subscriptionFormData: PropTypes.object.isRequired,
};

export default compose(
  observer,
)(SubscriptionCard);
